'use client';

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { cn } from '@lib/util/cn';
import BodyScrollLock from '@modules/common/components/body-scroll-lock';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { ChevronLeft, Menu as MenuIcon } from '@modules/common/icons';
import MobileSearchBar from '@modules/layout/templates/nav/mobile-search-bar';
import { Fragment, useCallback, useRef, useState } from 'react';
import type { NavCategory } from 'types/view-models';

import FooterActions from './footer-actions';
import MainNavPanel from './main-nav-panel';
import PanelHeader from './panel-header';
import TrustIndicators from './trust-indicators';

type SideMenuProps = {
  categories: NavCategory[];
};

const SideMenu = ({ categories }: SideMenuProps) => {
  const [open, setOpen] = useState(false);
  const [showLevel2, setShowLevel2] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => setShowLevel2(false), 300);
  }, []);

  const handleShowLevel2 = useCallback(() => setShowLevel2(true), []);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (delta > 60) {
      if (showLevel2) {
        setShowLevel2(false);
      } else {
        handleClose();
      }
    }
    touchStartX.current = null;
  };

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <BodyScrollLock enabled={open} />
        <div className="h-full flex">
          <div className="relative flex h-full">
            <button
              type="button"
              data-testid="nav-menu-button"
              className="relative cursor-pointer p-3 h-full flex items-center transition-colors duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset text-white hover:text-fg-accent-inverse-soft"
              aria-label="Open navigation menu"
              onClick={() => setOpen(true)}
            >
              <MenuIcon size="20" aria-hidden="true" />
            </button>
          </div>
        </div>

        <Transition show={open} as={Fragment}>
          <Dialog as="div" className="relative z-[50]" onClose={handleClose}>
            <TransitionChild
              as={Fragment}
              enter="transition-opacity ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-in duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="fixed inset-0 z-[50] bg-black/30 cursor-default"
                aria-hidden="true"
                data-testid="side-menu-backdrop"
              />
            </TransitionChild>

            <TransitionChild
              as={Fragment}
              enter="transition ease-out duration-300"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in duration-300"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel
                className="fixed inset-y-0 left-0 w-[85%] max-w-sm z-[51] shadow-xl bg-white flex flex-col"
                style={{ paddingLeft: 'env(safe-area-inset-left)' }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <div
                  data-testid="nav-menu-popup"
                  className="flex flex-col flex-1 min-h-0"
                  style={{ paddingTop: 'env(safe-area-inset-top)' }}
                >
                  <PanelHeader onClose={handleClose} />
                  <MobileSearchBar
                    className="border-b border-brand-neutral-200 shrink-0"
                    onNavigate={handleClose}
                  />

                  {/* Nav content — slides between Level 1 and Level 2 */}
                  <div className="flex-1 overflow-hidden relative">
                    {/* ─── Level 1 ─── */}
                    <MainNavPanel
                      showLevel2={showLevel2}
                      onShowLevel2={handleShowLevel2}
                      onClose={handleClose}
                    />

                    {/* ─── Level 2 ─── */}
                    <nav
                      id="side-menu-level2"
                      className={cn(
                        'absolute inset-0 flex flex-col overflow-hidden transition-transform duration-300 ease-in-out',
                        showLevel2 ? 'translate-x-0' : 'translate-x-full',
                      )}
                      aria-label="Category navigation"
                      aria-hidden={!showLevel2}
                      inert={!showLevel2}
                    >
                      <button
                        type="button"
                        onClick={() => setShowLevel2(false)}
                        aria-label="Back to main navigation"
                        className="text-sm cursor-pointer flex items-center gap-2 p-4 text-brand-neutral-500 border-b border-brand-neutral-200 hover:bg-surface-neutral-subtle transition-colors w-full shrink-0"
                      >
                        <ChevronLeft size="20" aria-hidden="true" />
                        Back
                      </button>

                      <div className="flex items-center justify-between p-4 border-b border-brand-neutral-200 bg-surface-neutral-subtle shrink-0">
                        <span className="font-medium text-brand-neutral-800">
                          Treatments &amp; Conditions
                        </span>
                        <LocalizedClientLink
                          href="/conditions"
                          onClick={handleClose}
                          className="text-sm text-brand-neutral-600 underline"
                        >
                          View all
                        </LocalizedClientLink>
                      </div>

                      <div className="flex-1 overflow-y-auto">
                        <div className="flex flex-col min-h-full">
                          <div>
                            {categories.map((category) => (
                              <LocalizedClientLink
                                key={category.id}
                                href={`/${category.handle}`}
                                onClick={handleClose}
                                className="flex items-center w-full px-4 py-4 border-b border-brand-neutral-200 text-fg-neutral-strong hover:bg-surface-neutral-subtle transition-colors"
                              >
                                {category.name}
                              </LocalizedClientLink>
                            ))}
                          </div>
                          <div className="flex-1" />
                          <TrustIndicators />
                        </div>
                      </div>
                    </nav>
                  </div>

                  <FooterActions onClose={handleClose} />
                </div>
              </DialogPanel>
            </TransitionChild>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
};

export default SideMenu;
